import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts/highstock";
import Loader from "../Loader";
import { calculateOnlineOfflinePercent, initModule } from "../../utils/utility";
import OnlineOfflineLegend from "./onlineOfflineLegend";
import createHighchartsOptions from "./HighChartsOptions";

import * as ExportingModule from "highcharts/modules/exporting";
import * as ExportDataModule from "highcharts/modules/export-data";
import NoData from "../Nodata";

// 3. Initialize
initModule(ExportingModule);
initModule(ExportDataModule);
function StepGraph(props) {
  const { data, loading, dateRange } = props;
  const [percent, setPercent] = useState({ online: 0, offline: 0 });
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const Title = "Connection status";

  useEffect(() => {
    if (!data || data.length === 0 || !containerRef.current) return;

    const chartData = data.map(item => ({
      x: item.ts,
      y: item.s,
      rsn: item.rsn,
    }));
    // Ensure chartData includes dateRange[0] and dateRange[1] points
    if (props.dateRange?.length === 2 && chartData?.length > 0) {
      // Convert to epoch if not already
      const toEpoch = val => {
        if (typeof val === "number") return val;
        // Try to parse as date string
        const d = new Date(val);
        return isNaN(d.getTime()) ? val : d.getTime();
      };
      const startEpoch = toEpoch(props.dateRange[0]);
      const endEpoch = toEpoch(props.dateRange[1]);
      // Give the startepoch as x-axis label for the first data
      chartData[0] = { ...chartData[0], x: startEpoch };
      // Add end point if not present
      if (chartData[chartData.length - 1].x !== endEpoch) {
        chartData.push({ x: endEpoch, y: chartData[chartData.length - 1].y, rsn: "" });
      }
    }
    const { percentOnline, percentOffline } = calculateOnlineOfflinePercent([...chartData], dateRange);
    setPercent({ online: percentOnline, offline: percentOffline });

    chartRef.current = Highcharts.stockChart(containerRef.current, createHighchartsOptions(Highcharts, chartData));

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [data]);

  return (
    <div
      className="relative w-full rounded-lg border border-gray-200 bg-white shadow-md p-4 overflow-hidden"
      style={{ height: "calc(30% - 10px)", flexShrink: 0, display: "flex", flexDirection: "column" }}
    >
      <div className="relative" style={{ flexShrink: 0 }}>
        <div style={{ color: "#333", fontWeight: "bold", fontSize: "16px" }}>{Title}</div>
        <div className="absolute top-30 left-0 z-10">
          {!loading && data && data.length > 0 ? (
            <OnlineOfflineLegend percentOnline={percent.online} percentOffline={percent.offline} />
          ) : null}
        </div>
      </div>
      <div style={{ flex: "1 1 0", minHeight: 0, position: "relative" }}>
        {!loading && (!data || data.length === 0) ? (
          <NoData />
        ) : loading ? (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.7)" }}
          >
            <Loader />
          </div>
        ) : null}
        <div ref={containerRef} className="w-full h-full" />
      </div>
    </div>
  );
}

StepGraph.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      ts: PropTypes.number.isRequired,
      s: PropTypes.number.isRequired,
      rsn: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  dateRange: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.object])),
};

export default StepGraph;
